import { api, unwrap } from "@lumberset/api-client";
import { type Evidence, flagKey, type TakeoffFlag } from "@lumberset/estimating";
import {
  AttentionList,
  attentionCounts,
  attentionSentence,
  CorrectionsEditor,
  type CorrectionsSave,
  CustomerRunStatus,
  dateTime,
  type EvidenceTarget,
  money,
  percent,
  quantity,
  type SettledFlagEntry,
  SettledFlags,
  SheetViewer,
  TakeoffLines,
  unitLabel,
  useAttention,
} from "@lumberset/takeoff-ui";
import {
  Badge,
  Button,
  Container,
  cn,
  EmptyState,
  SectionHeader,
  Skeleton,
  Stat,
  toast,
} from "@lumberset/ui";
import {
  ArrowDownTray,
  ArrowRight,
  ExclamationCircle,
  InformationCircle,
  PaperPlane,
  PencilSquare,
} from "@medusajs/icons";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Fragment, useMemo, useState } from "react";
import { CardBanner } from "../../../../components/card-banner";
import { Crumbs } from "../../../../components/crumbs";
import { TakeoffProgress } from "../../../../components/takeoff-progress";
import {
  billingSummaryQuery,
  budgetQuery,
  projectQuery,
  takeoffEventsQuery,
  takeoffQuery,
} from "../../../../lib/queries";

export const Route = createFileRoute("/_app/projects/$id/takeoffs/$runId")({
  loader: ({ context: { queryClient, yard }, params }) => {
    // What the screen asks for once it is drawn is asked for now, beside the Takeoff.
    void queryClient.prefetchQuery(billingSummaryQuery);
    if (yard.kind === "builder") void queryClient.prefetchQuery(budgetQuery(params.runId));
    return Promise.all([
      queryClient.ensureQueryData(projectQuery(params.id)),
      queryClient.ensureQueryData(takeoffQuery(params.runId)),
    ]);
  },
  component: TakeoffScreen,
});

const working = new Set(["queued", "running", "in_review"]);

/** Who last set the values the Takeoff was computed from. */
const originLabels: Record<string, string> = {
  reading: "as read from the drawings",
  reviewer: "corrected by our Reviewers",
  member: "corrected by your team",
};

function TakeoffScreen() {
  const { id, runId } = Route.useParams();
  const { yard, user } = Route.useRouteContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [viewing, setViewing] = useState<EvidenceTarget | null>(null);
  const [correcting, setCorrecting] = useState(false);
  // A Builder budgets and sends RFQs; a Yard prices from its Catalog into a Quote.
  const builder = yard.kind === "builder";

  const { data: parent } = useSuspenseQuery(projectQuery(id));
  const { data } = useSuspenseQuery({
    ...takeoffQuery(runId),
    refetchInterval: (query) => (working.has(query.state.data?.run.status ?? "") ? 3000 : false),
  });
  const { run, revision, settledFlags } = data;
  // Flags our Reviewers settled do not ask for attention again: they fold under one line.
  const attention = useMemo(() => {
    const byKey = new Map(settledFlags.map((item) => [item.key, item]));
    const open: TakeoffFlag[] = [];
    const settled: SettledFlagEntry[] = [];
    for (const flag of revision?.takeoff.flags ?? []) {
      const found = byKey.get(flagKey(flag));
      if (found) settled.push({ flag, resolution: found.resolution, note: found.note });
      else open.push(flag);
    }
    return { open, settled };
  }, [revision, settledFlags]);
  // Decisions first, assumptions as one row each, drawing notes folded under a count.
  const groups = useAttention(attention.open, revision?.buildingModel ?? null);
  const counts = groups ? attentionCounts(groups) : null;
  const toCheck = counts ? attentionSentence(counts) : null;

  const createQuote = useMutation({
    mutationFn: () => unwrap(api.quotes.$post({ json: { runId } })),
    onSuccess: async ({ quote, matched, lineCount }) => {
      await queryClient.invalidateQueries({ queryKey: ["quotes"] });
      await queryClient.invalidateQueries({ queryKey: ["projects", id] });
      toast.success(`Quote #${quote.number} drafted`, {
        description: `${matched} of ${lineCount} lines matched to your catalog.`,
      });
      await navigate({ to: "/quotes/$id", params: { id: quote.id } });
    },
    onError: (error) => toast.error(error.message),
  });

  // Correcting is free: it is the same Billable Quote, recomputed.
  const correct = useMutation({
    mutationFn: (body: CorrectionsSave) =>
      unwrap(api.takeoffs[":id"].corrections.$post({ param: { id: runId }, json: body })),
    onSuccess: async (result, body) => {
      await queryClient.invalidateQueries({ queryKey: ["takeoffs", runId] });
      toast.success("Takeoff recomputed. No charge for corrections.", {
        description: `${body.corrections.length} ${
          body.corrections.length === 1 ? "correction" : "corrections"
        } recorded. The list below is version ${result.revision.revision}.`,
      });
    },
    onError: (error) => toast.error(error.message),
  });

  const label = parent.planSets.find((set) => set.id === run.planSetId)?.label ?? "Drawings";
  const released = run.status === "released" && revision !== null;
  const showEvidence = (evidence: Evidence, what: string) =>
    setViewing({ planSetId: run.planSetId, evidence, label: what });

  return (
    <>
      <Crumbs
        trail={[
          { label: "Projects", to: "/projects" },
          { label: parent.project.name, to: "/projects/$id", params: { id } },
          { label: `Takeoff, ${label}` },
        ]}
      />

      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title={`Takeoff, ${label}`}
          description={
            revision
              ? `Ready ${dateTime(run.releasedAt)}, ${originLabels[revision.origin] ?? revision.origin}.`
              : `Started ${dateTime(run.createdAt)}`
          }
          actions={
            <>
              <CustomerRunStatus status={run.status} />
              {released ? (
                <Button size="small" variant="secondary" onClick={() => setCorrecting(true)}>
                  <PencilSquare /> Correct
                </Button>
              ) : null}
              {revision && builder ? (
                <Button size="small" asChild>
                  <Link to="/rfqs/new" search={{ runId }}>
                    <PaperPlane /> Request quotes
                  </Link>
                </Button>
              ) : null}
              {revision && !builder ? (
                <Button
                  size="small"
                  isLoading={createQuote.isPending}
                  onClick={() => createQuote.mutate()}
                >
                  Create quote
                </Button>
              ) : null}
            </>
          }
        />

        {revision ? (
          <div className="grid grid-cols-2 divide-ui-border-base max-lg:divide-y lg:grid-cols-4 lg:divide-x">
            <Stat label="Lines" value={revision.takeoff.summary.lineCount} />
            <Stat
              label="Board feet"
              value={quantity(Math.round(revision.takeoff.summary.boardFeet))}
            />
            <Stat
              label="Panels"
              value={quantity(revision.takeoff.summary.panelSheets)}
              hint="sheets"
            />
            <Stat
              label="Worth a look"
              value={percent(revision.takeoff.summary.lowConfidenceShare)}
              hint="of the materials sit on lines we are less sure of. Open those lines first."
            />
          </div>
        ) : null}

        {released ? (
          // What to do with a finished takeoff, in one line, before anything else.
          <div className="flex items-start gap-x-3 px-6 py-4">
            <span className="mt-0.5 shrink-0 text-ui-fg-interactive">
              <ArrowRight />
            </span>
            <p className="txt-small text-pretty text-ui-fg-subtle">
              <span className="txt-compact-small-plus text-ui-fg-base">Next:</span>{" "}
              {toCheck
                ? `look over what is worth your attention below (${toCheck.replace(/\.$/, "")}), then `
                : "look over the materials, then "}
              {builder
                ? "budget it below or send it to the yards you choose."
                : "create the quote. It is priced from your catalog."}{" "}
              Correcting anything here is free and recomputes the list.
            </p>
          </div>
        ) : null}
      </Container>

      {released ? (
        <CardBanner
          workspaceId={yard.id}
          returnTo={`/projects/${id}/takeoffs/${runId}`}
          confirmReturn
        />
      ) : null}

      {working.has(run.status) ? (
        <TakeoffProgress
          runId={runId}
          stage={run.stage}
          progress={run.progress}
          status={run.status}
          gated={run.gated}
          heldFor={run.heldFor}
          email={user.email}
        />
      ) : null}

      {run.status === "failed" ? (
        <Container className="p-0">
          <EmptyState
            icon={<ExclamationCircle />}
            title="We could not read these drawings"
            description={`${run.error ?? "Something stopped the takeoff."} You have not been charged, and our team has been told.`}
          />
        </Container>
      ) : null}

      {revision ? (
        <>
          {attention.open.length + attention.settled.length > 0 ? (
            <Container className="divide-y p-0">
              <SectionHeader
                title="Worth your attention"
                description={
                  counts && counts.decisions > 0
                    ? "Decisions first. Then what we assumed from your standards where the drawings were silent, and the notes we could not count."
                    : attention.open.length > 0
                      ? "What we assumed from your standards where the drawings were silent, and the notes we could not count. None of it changes a number by itself."
                      : "Nothing here needs you. Our Reviewers checked every note on this takeoff."
                }
              />
              {groups && revision ? (
                <AttentionList groups={groups} model={revision.buildingModel} />
              ) : null}
              <SettledFlags settled={attention.settled} />
            </Container>
          ) : null}

          {released && builder ? (
            <BudgetSection runId={runId} lines={revision.takeoff.lines} />
          ) : null}

          <Container className="divide-y overflow-hidden p-0">
            <SectionHeader
              title="Materials"
              description="Open any line to see how its quantity was worked out and where it was read."
              actions={
                released ? (
                  <>
                    <Button size="small" variant="secondary" asChild>
                      <a href={`/api/v1/takeoffs/${runId}/export.csv`} download>
                        <ArrowDownTray /> Download CSV
                      </a>
                    </Button>
                    <Button size="small" variant="secondary" asChild>
                      {/* A Builder's PDF carries the Budget Estimate beside each line. */}
                      <a
                        href={`/api/v1/takeoffs/${runId}/pdf${builder ? "?budget=1" : ""}`}
                        download
                      >
                        <ArrowDownTray /> Download PDF
                      </a>
                    </Button>
                  </>
                ) : null
              }
            />
            {revision.takeoff.lines.length === 0 ? (
              <EmptyState
                title="No materials"
                description="Nothing quantifiable was found in these drawings."
              />
            ) : (
              <TakeoffLines
                takeoff={revision.takeoff}
                model={revision.buildingModel}
                onShowEvidence={showEvidence}
              />
            )}
            {released ? (
              // The way on, where a person lands after reading the list: no scroll back to the top.
              <div className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="txt-small text-pretty text-ui-fg-subtle">
                  {builder
                    ? "Happy with the list? Send it to the yards you choose and compare their quotes side by side."
                    : "Happy with the list? Create the quote: every line is priced from your catalog, and you can change any price before sending."}
                </p>
                {builder ? (
                  <Button size="small" asChild className="shrink-0 max-sm:w-full">
                    <Link to="/rfqs/new" search={{ runId }}>
                      <PaperPlane /> Request quotes
                    </Link>
                  </Button>
                ) : (
                  <Button
                    size="small"
                    className="shrink-0 max-sm:w-full"
                    isLoading={createQuote.isPending}
                    onClick={() => createQuote.mutate()}
                  >
                    Create quote
                  </Button>
                )}
              </div>
            ) : null}
          </Container>
        </>
      ) : null}

      <SheetViewer
        target={viewing}
        onClose={() => setViewing(null)}
        fetchImage={(planSetId, page) =>
          unwrap(
            api["plan-sets"][":id"].sheets[":page"].image.$get({
              param: { id: planSetId, page: String(page) },
            }),
          )
        }
      />

      {released ? (
        <CorrectionsEditor
          open={correcting}
          onOpenChange={setCorrecting}
          buildingModel={revision.buildingModel}
          standards={revision.standards}
          baseline={revision.takeoff}
          by="member"
          onSave={(body) => correct.mutateAsync(body)}
          onShowEvidence={showEvidence}
        />
      ) : null}
    </>
  );
}

const sectionTitles: Record<string, string> = {
  walls: "Walls",
  floors: "Floors and ceilings",
  roof: "Roof",
  beams_posts: "Beams and posts",
  sheathing: "Sheathing and subfloor",
  hardware: "Hardware",
  other: "Other",
};

/** A reference price's date is a day, kept as that day's midnight in UTC, so it reads in UTC. */
const asOfFormat = new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeZone: "UTC" });
const asOfDay = (value: string) => asOfFormat.format(new Date(value));

/**
 * The Budget Estimate: the released lines priced with LumberSet's Reference
 * Prices. A guide for budgeting, never a quote, and it says so, with how old
 * its prices are and whether any are still our own starter estimates.
 */
function BudgetSection({
  runId,
  lines,
}: {
  runId: string;
  lines: {
    id: string;
    section: string;
    group: string;
    description: string;
    unit: string;
    orderQuantity: number;
  }[];
}) {
  const { data: budget, error } = useQuery(budgetQuery(runId));

  const priced = new Map(budget?.lines.map((line) => [line.lineId, line]) ?? []);
  const mixed =
    budget !== undefined &&
    budget.starterEstimates > 0 &&
    budget.starterEstimates < budget.pricedLines;
  const grouped: [string, typeof lines][] = [];
  for (const line of lines) {
    const last = grouped[grouped.length - 1];
    if (last && last[0] === line.section) last[1].push(line);
    else grouped.push([line.section, [line]]);
  }

  return (
    <Container className="divide-y overflow-hidden p-0">
      <SectionHeader
        title="Budget"
        description="What this takeoff might cost at typical prices for your region, before any yard has quoted it."
      />

      {error ? (
        <EmptyState
          className="py-10"
          title="We could not price this takeoff"
          description={error.message}
        />
      ) : !budget ? (
        <div className="flex flex-col gap-y-3 px-6 py-5">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-4 w-64" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 divide-ui-border-base max-lg:divide-y lg:grid-cols-4 lg:divide-x">
            <Stat
              label="Budget"
              value={money(budget.subtotalCents, budget.currency)}
              hint={
                budget.pricebookReviewed
                  ? `${budget.region} reference prices`
                  : "Reference prices not yet reviewed"
              }
            />
            <Stat label="Lines priced" value={budget.pricedLines} hint={`of ${lines.length}`} />
            <Stat
              label="No reference price"
              value={budget.unpricedLines}
              hint="Not in the budget total"
            />
            <Stat
              label="Takeoff version"
              value={budget.revision}
              hint="Prices the list as it stands now"
            />
          </div>

          {budget.pricesAsOf ? (
            <div
              className={cn(
                "txt-small flex items-start gap-x-2 px-6 py-3",
                budget.starterEstimates > 0
                  ? "bg-ui-tag-orange-bg text-ui-tag-orange-text"
                  : "text-ui-fg-subtle",
              )}
            >
              <InformationCircle className="mt-0.5 shrink-0" />
              <p>
                Reference prices as of {asOfDay(budget.pricesAsOf)}.
                {budget.starterEstimates > 0
                  ? " Some prices are LumberSet starter estimates, not a supplier's price."
                  : ""}
              </p>
            </div>
          ) : null}

          <div className="overflow-x-auto">
            <table className="txt-compact-small w-full min-w-[560px] text-ui-fg-subtle">
              <thead className="txt-compact-small-plus border-b border-ui-border-base bg-ui-bg-subtle text-left">
                <tr>
                  <th className="py-2.5 pl-6 pr-3 font-medium">Item</th>
                  <th className="w-28 px-3 py-2.5 text-right font-medium">Qty</th>
                  <th className="w-36 px-3 py-2.5 text-right font-medium">Reference price</th>
                  <th className="w-28 py-2.5 pl-3 pr-6 text-right font-medium">Budget</th>
                </tr>
              </thead>
              <tbody>
                {grouped.map(([section, sectionLines]) => (
                  <Fragment key={section}>
                    <tr className="border-b border-ui-border-base">
                      <td
                        colSpan={4}
                        className="txt-compact-xsmall-plus px-6 py-1.5 text-ui-fg-muted"
                      >
                        {sectionTitles[section] ?? section}
                      </td>
                    </tr>
                    {sectionLines.map((line) => {
                      const price = priced.get(line.id);
                      const unpriced = !price || price.totalCents === null;
                      return (
                        <tr
                          key={line.id}
                          className={cn(
                            "border-b border-ui-border-base last:border-b-0",
                            unpriced && "bg-ui-tag-orange-bg/40",
                          )}
                        >
                          <td className="py-2 pl-6 pr-3">
                            <div className="txt-compact-small-plus text-ui-fg-base">
                              {line.description}
                            </div>
                            <div className="txt-compact-xsmall flex items-center gap-x-2 text-ui-fg-muted">
                              {unpriced ? (
                                <Badge color="orange" className="whitespace-nowrap">
                                  No reference price
                                </Badge>
                              ) : price.reference ? (
                                <span className="code-label">{price.reference.code}</span>
                              ) : null}
                              {/* Only worth saying line by line when real prices and estimates mix. */}
                              {mixed && price?.reference?.source === "starter_estimate" ? (
                                <span className="whitespace-nowrap text-ui-tag-orange-text">
                                  starter estimate
                                </span>
                              ) : null}
                              <span>{line.group}</span>
                            </div>
                          </td>
                          <td className="tabular whitespace-nowrap px-3 py-2 text-right">
                            {quantity(line.orderQuantity)}{" "}
                            {unitLabel(line.unit, line.orderQuantity)}
                          </td>
                          <td className="tabular whitespace-nowrap px-3 py-2 text-right">
                            {unpriced || price.unitPriceCents === null
                              ? "-"
                              : `${money(price.unitPriceCents, budget.currency)} / ${line.unit}`}
                          </td>
                          <td className="tabular whitespace-nowrap py-2 pl-3 pr-6 text-right text-ui-fg-base">
                            {unpriced ? "-" : money(price.totalCents ?? 0, budget.currency)}
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="txt-small flex items-start gap-x-2 bg-ui-bg-subtle px-6 py-3 text-ui-fg-muted">
            <InformationCircle className="mt-0.5 shrink-0" />
            <p>{budget.disclaimer}</p>
          </div>
        </>
      )}
    </Container>
  );
}
